import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { DunningService } from '@/api';
import type { billing_PauseRequest } from '@/api/models/billing_PauseRequest';
import type { billing_OptionalReasonRequest } from '@/api/models/billing_OptionalReasonRequest';
import type { billing_ReasonRequest } from '@/api/models/billing_ReasonRequest';
import { dunningWorkflowKeys } from './useDunningWorkflows';
import { codeToMessage, errorToastText } from '@/lib/translateBackendError';
import { alreadyHandledMessage, isAlreadyHandledError } from '@/lib/alreadyHandled';

interface ApiError {
  status?: number;
  body?: { message?: string; code?: string };
}

type ConflictKind = 'approve' | 'cancel' | 'generic';

function translateError(err: unknown, kind: ConflictKind = 'generic'): string {
  const e = err as ApiError;
  if (e?.status === 409) {
    const code = e?.body?.code;
    // Backend signals an approve race with code="approve_in_progress" — the
    // email is already being approved or sent by another operator/scheduler.
    if (kind === 'approve' && code === 'approve_in_progress') {
      return i18n._(
        msg({
          id: 'dunning.actions.approve.errorInProgress',
          message: 'Email is already being approved or sent. Refreshing.',
        }),
      );
    }
    // Cancel arrived too late — the email row has already moved past `held`
    // (e.g., into sending/sent/failed/cancelled). Tell the user explicitly so
    // they don't keep retrying.
    if (kind === 'cancel') {
      return i18n._(
        msg({
          id: 'dunning.actions.cancel.errorTooLate',
          message: 'Cannot cancel: this email has already been sent or is being sent right now.',
        }),
      );
    }
  }
  const known = e?.body?.code ? codeToMessage[e.body.code] : undefined;
  if (known) return i18n._(known);
  // A colleague or the sweep moved the workflow first (409), or it is gone (404).
  if (isAlreadyHandledError(err)) {
    return i18n._(alreadyHandledMessage);
  }
  if (e?.status === 429) {
    return i18n._(msg({ id: 'dunning.action.error.rate-limited', message: 'Too many requests. Please slow down.' }));
  }
  return errorToastText(err, i18n, msg({ id: 'dunning.action.error.generic', message: 'Something went wrong.' }));
}

/** Toasts the failure and, on a lost race, reloads so "Already handled" shows the current state. */
function reportError(qc: ReturnType<typeof useQueryClient>, id: string, err: unknown, kind: ConflictKind = 'generic') {
  if (isAlreadyHandledError(err)) invalidateAll(qc, id);
  toast.error(translateError(err, kind));
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: dunningWorkflowKeys.all });
  if (id) {
    qc.invalidateQueries({ queryKey: dunningWorkflowKeys.detail(id) });
  }
}

export function usePauseDunning(workflowItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: billing_PauseRequest) =>
      DunningService.postApiV1DunningWorkflowsPause(workflowItemId, req),
    onSuccess: () => {
      invalidateAll(qc, workflowItemId);
      toast.success(i18n._(msg({ id: 'dunning.action.pause.success', message: 'Workflow paused.' })));
    },
    onError: (err) => reportError(qc, workflowItemId, err),
  });
}

export function useResumeDunning(workflowItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: billing_ReasonRequest) =>
      DunningService.postApiV1DunningWorkflowsResume(workflowItemId, req),
    onSuccess: () => {
      invalidateAll(qc, workflowItemId);
      toast.success(i18n._(msg({ id: 'dunning.action.resume.success', message: 'Workflow resumed.' })));
    },
    onError: (err) => reportError(qc, workflowItemId, err),
  });
}

export function useSendNowDunning(workflowItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req?: billing_OptionalReasonRequest) =>
      DunningService.postApiV1DunningWorkflowsSendNow(workflowItemId, req),
    onSuccess: () => {
      invalidateAll(qc, workflowItemId);
      toast.success(i18n._(msg({ id: 'dunning.action.send-now.success', message: 'Reminder queued.' })));
    },
    // Send-now (for awaiting_approval blockers) collides with another
    // operator approving the same draft. Backend returns 409 with
    // code=approve_in_progress; reportError refreshes the drawer so the
    // FSM/blocker view re-syncs to current truth.
    onError: (err) => reportError(qc, workflowItemId, err, 'approve'),
  });
}

export function useHandoverDunning(workflowItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: billing_ReasonRequest) =>
      DunningService.postApiV1DunningWorkflowsHandover(workflowItemId, req),
    onSuccess: () => {
      invalidateAll(qc, workflowItemId);
      toast.success(i18n._(msg({ id: 'dunning.action.handover.success', message: 'Marked as collections handover.' })));
    },
    onError: (err) => reportError(qc, workflowItemId, err),
  });
}

export function useCancelDunning(workflowItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Backend binds OptionalReasonRequest (no validate:"required") because
    // the awaiting_approval cancel-this-email path accepts an empty reason.
    // The legacy FSM-cancel modal still passes a populated reason; keeping
    // the contract optional preserves both call sites with one mutation.
    mutationFn: (req: billing_OptionalReasonRequest) =>
      DunningService.postApiV1DunningWorkflowsCancel(workflowItemId, req),
    onSuccess: () => {
      invalidateAll(qc, workflowItemId);
      toast.success(i18n._(msg({ id: 'dunning.action.cancel.success', message: 'Workflow cancelled.' })));
    },
    // Cancel arrived after the email left `held`: backend rejects with 409.
    // reportError refreshes so the user sees the new state instead of retrying.
    onError: (err) => reportError(qc, workflowItemId, err, 'cancel'),
  });
}

export function useRetryDunningSend(workflowItemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => DunningService.postApiV1DunningWorkflowsRetry(workflowItemId),
    onSuccess: (data) => {
      invalidateAll(qc, workflowItemId);
      if (data.status === 'sent') {
        toast.success(i18n._(msg({ id: 'dunning.blocker.toast.sent', message: 'Sent successfully.' })));
      } else {
        toast.error(
          i18n._(
            msg({ id: 'dunning.blocker.toast.failed', message: 'Still failed: {reason}' }),
            { reason: data.reason ?? '' }
          )
        );
      }
    },
    onError: (err) => reportError(qc, workflowItemId, err),
  });
}
